/**
 * Validação de emails partilhada (nível 2).
 *
 *  - `emailSyntaxError()` — validação offline instantânea (sintaxe rigorosa +
 *    domínios descartáveis). Usar em qualquer campo de email.
 *  - `suggestEmail()` — sugere correcção de typos comuns ("gmial.con" →
 *    "gmail.com").
 *  - `verifyEmail()` — chama o backend (`POST /api/public/validate-email`)
 *    que confirma se o domínio existe e recebe email (MX). ~200-800ms.
 *
 * Não garante que a caixa existe (isso exigiria serviço pago), mas apanha
 * erros de escrita, domínios inexistentes e emails temporários.
 */

export const EMAIL_RE =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

const DISPOSABLE = new Set([
  "10minutemail.com", "mailinator.com", "yopmail.com", "yopmail.fr",
  "guerrillamail.com", "sharklasers.com", "grr.la", "temp-mail.org",
  "tempmail.com", "trashmail.com", "getnada.com", "maildrop.cc",
  "dropmail.me", "throwaway.email", "fakeinbox.com", "mailcatch.com",
  "1secmail.com", "moakt.com", "mohmal.com", "emailondeck.com",
]);

const COMMON_DOMAINS = [
  "gmail.com", "hotmail.com", "outlook.com", "outlook.pt", "live.com.pt",
  "live.com", "msn.com", "yahoo.com", "yahoo.es", "icloud.com", "me.com",
  "sapo.pt", "mail.pt", "clix.pt", "iol.pt", "netcabo.pt", "gmail.pt",
  "hotmail.es", "hotmail.fr", "hotmail.co.uk", "protonmail.com", "proton.me",
  "aeiou.pt", "portugalmail.pt", "telepac.pt", "nos.pt", "meo.pt",
  "ihavethepower.net", "solfut.com",
];

const TLD_TYPOS = {
  con: "com", cmo: "com", comm: "com", vom: "com", xom: "com", co: "com",
  cm: "com", om: "com", copm: "com", comn: "com",
  p: "pt", ot: "pt", ppt: "pt", tp: "pt", ptt: "pt",
};

function levenshtein(a, b) {
  if (a === b) return 0;
  if (a.length < b.length) [a, b] = [b, a];
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const cur = [i];
    for (let j = 1; j <= b.length; j += 1) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length];
}

/** Sugere o email corrigido (ou null se parecer bem escrito). */
export function suggestEmail(email) {
  const e = String(email || "").trim().toLowerCase();
  if (!e.includes("@")) return null;
  const [local, domainRaw] = e.split("@");
  if (!local || !domainRaw) return null;
  let domain = domainRaw;
  if (COMMON_DOMAINS.includes(domain)) return null;
  const parts = domain.split(".");
  if (parts.length >= 2 && TLD_TYPOS[parts[parts.length - 1]]) {
    parts[parts.length - 1] = TLD_TYPOS[parts[parts.length - 1]];
    domain = parts.join(".");
    if (COMMON_DOMAINS.includes(domain)) return `${local}@${domain}`;
  }
  let best = null;
  let bestDist = 99;
  COMMON_DOMAINS.forEach((cand) => {
    const d = levenshtein(domain, cand);
    if (d < bestDist) { best = cand; bestDist = d; }
  });
  const limit = domain.length <= 6 ? 1 : 2;
  return bestDist > 0 && bestDist <= limit ? `${local}@${best}` : null;
}

/** Devolve mensagem de erro de sintaxe ou null quando está válido. */
export function emailSyntaxError(email) {
  const e = String(email || "").trim();
  if (!e) return "Indique o email.";
  if (/\s/.test(e)) return "O email não pode conter espaços.";
  if ((e.match(/@/g) || []).length !== 1) return "O email tem de ter exactamente um @.";
  if (!EMAIL_RE.test(e)) return "Email inválido. Verifique o formato (ex.: nome@dominio.pt).";
  if (DISPOSABLE.has(e.split("@")[1].toLowerCase())) {
    return "Emails temporários/descartáveis não são aceites.";
  }
  return null;
}

const cache = new Map();

/**
 * Validação completa: sintaxe local + confirmação do domínio no servidor.
 * Nunca lança — em caso de falha de rede devolve `{ valid: true }` para não
 * bloquear o utilizador por causa da nossa infra.
 */
export async function verifyEmail(email) {
  const e = String(email || "").trim().toLowerCase();
  const syntax = emailSyntaxError(e);
  if (syntax) {
    return { valid: false, reason: "syntax", message: syntax, suggestion: suggestEmail(e) || "" };
  }
  if (cache.has(e)) return cache.get(e);
  const base = process.env.REACT_APP_BACKEND_URL;
  try {
    const r = await fetch(`${base}/api/public/validate-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: e }),
    });
    const d = await r.json();
    const out = {
      valid: d.valid !== false,
      reason: d.reason || "",
      message: d.message || "",
      suggestion: d.suggestion || suggestEmail(e) || "",
    };
    cache.set(e, out);
    return out;
  } catch (_err) {
    return { valid: true, reason: "", message: "", suggestion: suggestEmail(e) || "" };
  }
}
